import '@/assets/css/global.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AxiosSessionHandlerProvider } from './components/AxiosSessionHandlerProvider/AxiosSessionHandlerProvider';
import {
  posthogHost,
  posthogPublicKey,
  sentryDSN,
  sentryEnvironment,
} from './constants/environment';
import { GlobalContextProvider } from './context/Global/GlobalContext';
import { SnackbarProvider } from './context/Snackbar/SnackbarContext';
import Routes from './routes';
import ThemeWrapper from './theme/themeWrapper';

const isLocalhost = window.location.href.includes('localhost:');
const integrations = [Sentry.browserTracingIntegration()];
if (!isLocalhost) integrations.push(Sentry.replayIntegration());

// Init Sentry SDK
Sentry.init({
  dsn: sentryDSN,
  integrations: integrations,
  // Set sentry environment
  environment: sentryEnvironment,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  tracesSampleRate: 1.0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: !isLocalhost,
});

// React Query client
const queryClient = new QueryClient();

const postHogOptions = {
  api_host: posthogHost,
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <PostHogProvider apiKey={posthogPublicKey} options={postHogOptions}>
      <ThemeWrapper isDark={false}>
        <SnackbarProvider>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <GlobalContextProvider>
                <BrowserRouter>
                  <AxiosSessionHandlerProvider>
                    <Routes />
                  </AxiosSessionHandlerProvider>
                </BrowserRouter>
              </GlobalContextProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeWrapper>
    </PostHogProvider>
  </React.StrictMode>
);
